/* eslint-disable react-hooks/exhaustive-deps */
import {
    Box,
    Text,
    Button,
    VStack,
    SimpleGrid,
    Input,
    FormControl,
    InputGroup,
    InputLeftElement,
    Flex,
} from '@chakra-ui/react';
import { AiOutlineMail } from 'react-icons/ai';
import SearchableSelect from 'components/SearchableSelect';
import CustomSelect from 'components/CustomSelect';
import { useCustomers, useCustomersMutations } from 'hooks/useCustomers';
import {
    useGetCountries,
    useGetStatesByCountryId,
    useGetCitiesByStateId,
} from 'hooks/useGetLocations';
import useDebounce from 'hooks/useDebounce';
import { useEffect, useState } from 'react';
import { Formik } from 'formik';
import * as Yup from 'yup';
import toast from 'react-hot-toast';
import { useSelector } from 'react-redux';

const NAVY = '#1B3B5F';
const NAVY2 = '#162f4e';
const TEAL = '#0D9488';
const SLATE = '#F8FAFC';
const BORDER = '#E2E8F0';

const phoneRegExp =
    /^(\+?[0-9]{1,4}|\\!)?[-.\s]?[(]?[0-9]{2,4}[)]?[-.\s]?[0-9]{3,4}[-.\s]?[0-9]{4,6}$/;
const emailRegExp = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

const newCustomerSchema = Yup.object().shape({
    first_name: Yup.string()
        .required('First Name is required')
        .min(2, 'Must be at least 2 characters')
        .max(50, 'Must not exceed 50 characters'),
    last_name: Yup.string()
        .required('Last Name is required')
        .min(2, 'Must be at least 2 characters')
        .max(50, 'Must not exceed 50 characters'),
    email: Yup.string().required('Email is required').matches(emailRegExp, 'Invalid email format'),
    phone: Yup.string()
        .required('Phone is required')
        .matches(phoneRegExp, 'Phone number is not valid'),
    line1: Yup.string().required('Address is required'),
    country: Yup.object().required('Country is required').nullable(),
    state: Yup.string().required('State is required').nullable(),
    city: Yup.string().required('City is required').nullable(),
});

const newAddressSchema = Yup.object().shape({
    line1: Yup.string().required('Address is required'),
    country: Yup.object().required('Country is required').nullable(),
    state: Yup.string().required('State is required').nullable(),
    city: Yup.string().required('City is required').nullable(),
    phone: Yup.string()
        .required('Phone is required')
        .matches(phoneRegExp, 'Phone number is not valid'),
});

const FieldLabel = ({ children, required }) => (
    <Text
        fontSize="xs"
        fontWeight="700"
        color="gray.600"
        textTransform="uppercase"
        letterSpacing="0.04em"
        mb={1}>
        {children}
        {required && (
            <Text as="span" color="red.400" ml={1}>
                *
            </Text>
        )}
    </Text>
);

const hasValidAddress = (customer) =>
    customer &&
    customer.cf_line_address?.trim() !== '' &&
    customer.cf_city?.trim() !== '' &&
    customer.cf_state?.trim() !== '' &&
    customer.cf_country?.trim() !== '';

const normalizePhone = (phone, countryCode) => {
    if (!phone) return '';
    let cleaned = phone.replace(/\D/g, '');
    if (countryCode && cleaned.startsWith(String(countryCode))) {
        cleaned = cleaned.slice(String(countryCode).length);
    }
    if (cleaned.startsWith('0')) cleaned = cleaned.slice(1);
    return cleaned;
};

export default function CustomerStep({ onConfirm, onSkip }) {
    const [view, setView] = useState('main');
    const [searchTerm, setSearchTerm] = useState('');
    const [selectedCustomer, setSelectedCustomer] = useState(null);
    const [selectedLocations, setSelectedLocations] = useState({
        country: null,
        state: null,
        city: null,
    });

    const debouncedSearch = useDebounce(searchTerm, 700);
    const { fetchCustomers, customers, isLoading } = useCustomers();
    const { createCustomer, isPending: isCreating } = useCustomersMutations();
    const verifyReducer = useSelector((s) => s.verifyReducer);

    const countries = useGetCountries();
    const states = useGetStatesByCountryId(selectedLocations?.country?.value);
    const cities = useGetCitiesByStateId(
        selectedLocations?.state?.value,
        selectedLocations?.country?.value,
    );

    useEffect(() => {
        fetchCustomers(debouncedSearch);
    }, [debouncedSearch]);

    const handleUseExistingAddress = () => {
        if (!selectedCustomer) return;
        onConfirm({
            customer_id: selectedCustomer.contact_id,
            delivery_address: {
                first_name: selectedCustomer.first_name ?? '',
                last_name: selectedCustomer.last_name ?? '',
                email: selectedCustomer.email ?? '',
                phone: selectedCustomer.phone ?? '',
                line1: selectedCustomer.cf_line_address ?? '',
                city: selectedCustomer.cf_city ?? '',
                state: selectedCustomer.cf_state ?? '',
                country: selectedCustomer.cf_country ?? '',
                is_residential: true,
                useExistingAddress: true,
            },
            customer: selectedCustomer,
        });
    };

    // NEW: confirm with the fresh address typed into the form
    const handleConfirmNewAddress = (values) => {
        const trimmedPhone = normalizePhone(values.phone, values.country?.phonecode);
        const formattedPhone = values.country?.phonecode
            ? `+${values.country.phonecode}${trimmedPhone}`
            : values.phone;

        onConfirm({
            customer_id: selectedCustomer?.contact_id ?? null,
            delivery_address: {
                first_name: selectedCustomer?.first_name ?? '',
                last_name: selectedCustomer?.last_name ?? '',
                email: selectedCustomer?.email ?? '',
                phone: formattedPhone || selectedCustomer?.phone || '',
                line1: values.line1,
                city: values.city,
                state: values.state,
                country: values.country?.value ?? '',
                is_residential: true,
                useExistingAddress: false,
            },
            customer: selectedCustomer ?? null,
        });
    };

    const handleCreateCustomer = async (values) => {
        const trimmedPhone = normalizePhone(values.phone, values.country?.phonecode);
        const formattedPhone = values.country?.phonecode
            ? `+${values.country.phonecode}${trimmedPhone}`
            : values.phone;

        const customerData = {
            first_name: values.first_name,
            last_name: values.last_name,
            email: values.email,
            phone: formattedPhone,
            line1: values.line1,
            state: values.state,
            city: values.city,
            country: values.country?.value ?? '',
            is_residential: true,
            metadata: { customerId: verifyReducer?.verifiedUser?.data?.user?.id },
        };

        try {
            const resp = await createCustomer(customerData);
            if (!resp) throw new Error('Failed to create customer');

            const newContactId = resp?.contact?.contact_id ?? resp?.zoho_id ?? null;

            onConfirm({
                customer_id: newContactId,
                delivery_address: {
                    first_name: values.first_name,
                    last_name: values.last_name,
                    email: values.email,
                    phone: formattedPhone,
                    line1: values.line1,
                    city: values.city,
                    state: values.state,
                    country: values.country?.value ?? '',
                    is_residential: true,
                    useExistingAddress: false,
                },
                customer: { ...customerData, contact_id: newContactId },
            });
        } catch (err) {
            console.error(err);
            toast.error(err?.message ?? 'Failed to create customer. Please try again.');
        }
    };

    if (view === 'address') {
        const canUseExisting = hasValidAddress(selectedCustomer);
        return (
            <VStack spacing={4} align="stretch">
                <Text fontSize="sm" fontWeight="700" color={NAVY}>
                    Address for {selectedCustomer?.first_name} {selectedCustomer?.last_name}
                </Text>

                {canUseExisting && (
                    <Box
                        p={3}
                        bg={SLATE}
                        borderRadius="xl"
                        border={`1px solid ${BORDER}`}
                        fontSize="sm"
                        color="gray.600">
                        <Text fontWeight="600" color={NAVY} mb={1} fontSize="xs">
                            Existing address on file:
                        </Text>
                        {[
                            selectedCustomer.cf_line_address,
                            selectedCustomer.cf_city,
                            selectedCustomer.cf_state,
                            selectedCustomer.cf_country,
                        ]
                            .filter(Boolean)
                            .join(', ')}
                    </Box>
                )}

                {!canUseExisting && (
                    <Box
                        p={3}
                        bg="orange.50"
                        borderRadius="xl"
                        border="1px solid"
                        borderColor="orange.200"
                        fontSize="xs"
                        color="orange.700">
                        No complete address found for this customer. Please enter a new address.
                    </Box>
                )}

                {canUseExisting && (
                    <Button
                        bg={NAVY}
                        color="white"
                        _hover={{ bg: NAVY2 }}
                        borderRadius="xl"
                        onClick={handleUseExistingAddress}
                        fontWeight="600"
                        fontSize="sm">
                        Use Existing Address
                    </Button>
                )}
                <Button
                    variant={canUseExisting ? 'outline' : 'solid'}
                    bg={canUseExisting ? undefined : NAVY}
                    color={canUseExisting ? undefined : 'white'}
                    _hover={canUseExisting ? undefined : { bg: NAVY2 }}
                    borderColor={NAVY}
                    borderRadius="xl"
                    onClick={() => setView('new-address')}
                    fontWeight="600"
                    fontSize="sm">
                    Enter New Address
                </Button>
                <Button
                    variant="ghost"
                    size="sm"
                    color="gray.400"
                    onClick={() => {
                        setView('existing');
                        setSelectedCustomer(null);
                    }}>
                    ← Back
                </Button>
            </VStack>
        );
    }

    if (view === 'new-address') {
        return (
            <Box>
                <Flex align="center" justify="space-between" mb={4}>
                    <Box>
                        <Text fontSize="sm" fontWeight="700" color={NAVY}>
                            New Shipping Address
                        </Text>
                        <Text fontSize="xs" color="gray.500" mt="2px">
                            For {selectedCustomer?.first_name} {selectedCustomer?.last_name}
                        </Text>
                    </Box>
                    <Button
                        variant="ghost"
                        size="sm"
                        color="gray.400"
                        onClick={() => setView('address')}>
                        ← Back
                    </Button>
                </Flex>

                <Formik
                    initialValues={{
                        line1: '',
                        country: null,
                        state: '',
                        city: '',
                        phone: '',
                    }}
                    validationSchema={newAddressSchema}
                    onSubmit={(values, { setSubmitting }) => {
                        handleConfirmNewAddress(values);
                        setSubmitting(false);
                    }}>
                    {({
                        values,
                        errors,
                        touched,
                        handleChange,
                        handleBlur,
                        handleSubmit,
                        isSubmitting,
                        setFieldValue,
                    }) => (
                        <form onSubmit={handleSubmit}>
                            <VStack spacing={3} align="stretch">
                                <SimpleGrid columns={{ base: 1, md: 2 }} gap={3}>
                                    <FormControl gridColumn={{ md: 'span 2' }}>
                                        <FieldLabel required>Address Line 1</FieldLabel>
                                        <Input
                                            size="sm"
                                            name="line1"
                                            placeholder="123 Street, Area"
                                            value={values.line1}
                                            onChange={handleChange}
                                            onBlur={handleBlur}
                                            borderRadius="lg"
                                            bg={SLATE}
                                            border={`1px solid ${BORDER}`}
                                            _focus={{ borderColor: TEAL }}
                                        />
                                        {touched.line1 && errors.line1 && (
                                            <Text fontSize="10px" color="red.500" mt={1}>
                                                {errors.line1}
                                            </Text>
                                        )}
                                    </FormControl>

                                    <FormControl>
                                        <FieldLabel required>Country</FieldLabel>
                                        <CustomSelect
                                            placeholder="Select Country"
                                            fontSize="sm"
                                            name="country"
                                            onChange={(e) => {
                                                const selected = JSON.parse(e.target.value);
                                                setFieldValue('country', selected);
                                                setFieldValue('state', '');
                                                setFieldValue('city', '');
                                                setSelectedLocations({
                                                    country: selected,
                                                    state: null,
                                                    city: null,
                                                });
                                            }}
                                            disabled={!countries?.length}
                                            options={(countries ?? []).map((c) => ({
                                                label: c.name,
                                                value: c.isoCode,
                                                phonecode: c.phonecode,
                                            }))}
                                        />
                                        {touched.country && errors.country && (
                                            <Text fontSize="10px" color="red.500" mt={1}>
                                                {errors.country}
                                            </Text>
                                        )}
                                    </FormControl>

                                    <FormControl>
                                        <FieldLabel required>State</FieldLabel>
                                        <CustomSelect
                                            placeholder="Select State"
                                            fontSize="sm"
                                            name="state"
                                            onChange={(e) => {
                                                const selected = JSON.parse(e.target.value);
                                                setFieldValue('state', selected.label);
                                                setFieldValue('city', '');
                                                setSelectedLocations((prev) => ({
                                                    ...prev,
                                                    state: selected,
                                                    city: null,
                                                }));
                                            }}
                                            disabled={!states?.length}
                                            options={(states ?? []).map((s) => ({
                                                label: s.name,
                                                value: s.isoCode,
                                            }))}
                                        />
                                        {touched.state && errors.state && (
                                            <Text fontSize="10px" color="red.500" mt={1}>
                                                {errors.state}
                                            </Text>
                                        )}
                                    </FormControl>

                                    <FormControl>
                                        <FieldLabel required>City</FieldLabel>
                                        <CustomSelect
                                            placeholder="Select City"
                                            fontSize="sm"
                                            name="city"
                                            onChange={(e) => {
                                                const selected = JSON.parse(e.target.value);
                                                setFieldValue('city', selected.label);
                                                setSelectedLocations((prev) => ({
                                                    ...prev,
                                                    city: selected,
                                                }));
                                            }}
                                            disabled={!cities?.length}
                                            options={(cities ?? []).map((c) => ({
                                                label: c.name,
                                                value: c.name,
                                            }))}
                                        />
                                        {touched.city && errors.city && (
                                            <Text fontSize="10px" color="red.500" mt={1}>
                                                {errors.city}
                                            </Text>
                                        )}
                                    </FormControl>

                                    <FormControl gridColumn={{ md: 'span 2' }}>
                                        <FieldLabel required>Phone Number</FieldLabel>
                                        <InputGroup>
                                            {values.country?.phonecode && (
                                                <InputLeftElement
                                                    pointerEvents="none"
                                                    h="32px"
                                                    w="48px">
                                                    <Text fontSize="xs" color="gray.500">
                                                        +{values.country.phonecode}
                                                    </Text>
                                                </InputLeftElement>
                                            )}
                                            <Input
                                                size="sm"
                                                name="phone"
                                                type="tel"
                                                placeholder={
                                                    selectedCustomer?.phone
                                                        ? selectedCustomer.phone
                                                        : '8012345678'
                                                }
                                                value={values.phone}
                                                onChange={handleChange}
                                                onBlur={handleBlur}
                                                pl={values.country?.phonecode ? '52px' : undefined}
                                                borderRadius="lg"
                                                bg={SLATE}
                                                border={`1px solid ${BORDER}`}
                                                _focus={{ borderColor: TEAL }}
                                            />
                                        </InputGroup>
                                        {touched.phone && errors.phone && (
                                            <Text fontSize="10px" color="red.500" mt={1}>
                                                {errors.phone}
                                            </Text>
                                        )}
                                        <Text fontSize="10px" color="gray.400" mt={1}>
                                            Contact phone for this delivery
                                        </Text>
                                    </FormControl>
                                </SimpleGrid>

                                <Button
                                    type="submit"
                                    bg={NAVY}
                                    color="white"
                                    _hover={{ bg: NAVY2 }}
                                    borderRadius="xl"
                                    isLoading={isSubmitting}
                                    loadingText="Saving…"
                                    mt={2}>
                                    Use This Address & Continue
                                </Button>
                            </VStack>
                        </form>
                    )}
                </Formik>
            </Box>
        );
    }

    // ── VIEW: create brand new customer ────────────────────────────────────────
    if (view === 'new') {
        return (
            <Box>
                <Flex align="center" justify="space-between" mb={4}>
                    <Text fontSize="sm" fontWeight="700" color={NAVY}>
                        New Customer Details
                    </Text>
                    <Button
                        variant="ghost"
                        size="sm"
                        color="gray.400"
                        onClick={() => setView('main')}>
                        ← Back
                    </Button>
                </Flex>

                <Formik
                    initialValues={{
                        first_name: '',
                        last_name: '',
                        email: '',
                        phone: '',
                        line1: '',
                        country: null,
                        state: '',
                        city: '',
                    }}
                    validationSchema={newCustomerSchema}
                    onSubmit={async (values, { setSubmitting }) => {
                        setSubmitting(true);
                        await handleCreateCustomer(values);
                        setSubmitting(false);
                    }}>
                    {({
                        values,
                        errors,
                        touched,
                        handleChange,
                        handleBlur,
                        handleSubmit,
                        isSubmitting,
                        setFieldValue,
                    }) => (
                        <form onSubmit={handleSubmit}>
                            <VStack spacing={3} align="stretch">
                                <SimpleGrid columns={{ base: 1, md: 2 }} gap={3}>
                                    <FormControl>
                                        <FieldLabel required>First Name</FieldLabel>
                                        <Input
                                            size="sm"
                                            name="first_name"
                                            placeholder="John"
                                            value={values.first_name}
                                            onChange={handleChange}
                                            onBlur={handleBlur}
                                            borderRadius="lg"
                                            bg={SLATE}
                                            border={`1px solid ${BORDER}`}
                                            _focus={{ borderColor: TEAL }}
                                        />
                                        {touched.first_name && errors.first_name && (
                                            <Text fontSize="10px" color="red.500" mt={1}>
                                                {errors.first_name}
                                            </Text>
                                        )}
                                    </FormControl>

                                    <FormControl>
                                        <FieldLabel required>Last Name</FieldLabel>
                                        <Input
                                            size="sm"
                                            name="last_name"
                                            placeholder="Doe"
                                            value={values.last_name}
                                            onChange={handleChange}
                                            onBlur={handleBlur}
                                            borderRadius="lg"
                                            bg={SLATE}
                                            border={`1px solid ${BORDER}`}
                                            _focus={{ borderColor: TEAL }}
                                        />
                                        {touched.last_name && errors.last_name && (
                                            <Text fontSize="10px" color="red.500" mt={1}>
                                                {errors.last_name}
                                            </Text>
                                        )}
                                    </FormControl>

                                    <FormControl>
                                        <FieldLabel required>Email</FieldLabel>
                                        <InputGroup>
                                            <InputLeftElement pointerEvents="none" h="32px">
                                                <AiOutlineMail color="gray" size={14} />
                                            </InputLeftElement>
                                            <Input
                                                size="sm"
                                                name="email"
                                                type="email"
                                                placeholder="john@example.com"
                                                value={values.email}
                                                onChange={handleChange}
                                                onBlur={handleBlur}
                                                borderRadius="lg"
                                                bg={SLATE}
                                                border={`1px solid ${BORDER}`}
                                                _focus={{ borderColor: TEAL }}
                                            />
                                        </InputGroup>
                                        {touched.email && errors.email && (
                                            <Text fontSize="10px" color="red.500" mt={1}>
                                                {errors.email}
                                            </Text>
                                        )}
                                    </FormControl>

                                    <FormControl>
                                        <FieldLabel required>Country</FieldLabel>
                                        <CustomSelect
                                            placeholder="Select Country"
                                            fontSize="sm"
                                            name="country"
                                            onChange={(e) => {
                                                const selected = JSON.parse(e.target.value);
                                                setFieldValue('country', selected);
                                                setFieldValue('state', '');
                                                setFieldValue('city', '');
                                                setSelectedLocations({
                                                    country: selected,
                                                    state: null,
                                                    city: null,
                                                });
                                            }}
                                            disabled={!countries?.length}
                                            options={(countries ?? []).map((c) => ({
                                                label: c.name,
                                                value: c.isoCode,
                                                phonecode: c.phonecode,
                                            }))}
                                        />
                                        {touched.country && errors.country && (
                                            <Text fontSize="10px" color="red.500" mt={1}>
                                                {errors.country}
                                            </Text>
                                        )}
                                    </FormControl>

                                    <FormControl>
                                        <FieldLabel required>Phone Number</FieldLabel>
                                        <InputGroup>
                                            {values.country?.phonecode && (
                                                <InputLeftElement
                                                    pointerEvents="none"
                                                    h="32px"
                                                    w="48px">
                                                    <Text fontSize="xs" color="gray.500">
                                                        +{values.country.phonecode}
                                                    </Text>
                                                </InputLeftElement>
                                            )}
                                            <Input
                                                size="sm"
                                                name="phone"
                                                type="tel"
                                                placeholder="8012345678"
                                                value={values.phone}
                                                onChange={handleChange}
                                                onBlur={handleBlur}
                                                pl={values.country?.phonecode ? '52px' : undefined}
                                                borderRadius="lg"
                                                bg={SLATE}
                                                border={`1px solid ${BORDER}`}
                                                _focus={{ borderColor: TEAL }}
                                            />
                                        </InputGroup>
                                        {touched.phone && errors.phone && (
                                            <Text fontSize="10px" color="red.500" mt={1}>
                                                {errors.phone}
                                            </Text>
                                        )}
                                    </FormControl>

                                    <FormControl gridColumn={{ md: 'span 2' }}>
                                        <FieldLabel required>Address Line 1</FieldLabel>
                                        <Input
                                            size="sm"
                                            name="line1"
                                            placeholder="123 Street, Area"
                                            value={values.line1}
                                            onChange={handleChange}
                                            onBlur={handleBlur}
                                            borderRadius="lg"
                                            bg={SLATE}
                                            border={`1px solid ${BORDER}`}
                                            _focus={{ borderColor: TEAL }}
                                        />
                                        {touched.line1 && errors.line1 && (
                                            <Text fontSize="10px" color="red.500" mt={1}>
                                                {errors.line1}
                                            </Text>
                                        )}
                                    </FormControl>

                                    <FormControl>
                                        <FieldLabel required>State</FieldLabel>
                                        <CustomSelect
                                            placeholder="Select State"
                                            fontSize="sm"
                                            name="state"
                                            onChange={(e) => {
                                                const selected = JSON.parse(e.target.value);
                                                setFieldValue('state', selected.label);
                                                setFieldValue('city', '');
                                                setSelectedLocations((prev) => ({
                                                    ...prev,
                                                    state: selected,
                                                    city: null,
                                                }));
                                            }}
                                            disabled={!states?.length}
                                            options={(states ?? []).map((s) => ({
                                                label: s.name,
                                                value: s.isoCode,
                                            }))}
                                        />
                                        {touched.state && errors.state && (
                                            <Text fontSize="10px" color="red.500" mt={1}>
                                                {errors.state}
                                            </Text>
                                        )}
                                    </FormControl>

                                    <FormControl>
                                        <FieldLabel required>City</FieldLabel>
                                        <CustomSelect
                                            placeholder="Select City"
                                            fontSize="sm"
                                            name="city"
                                            onChange={(e) => {
                                                const selected = JSON.parse(e.target.value);
                                                setFieldValue('city', selected.label);
                                                setSelectedLocations((prev) => ({
                                                    ...prev,
                                                    city: selected,
                                                }));
                                            }}
                                            disabled={!cities?.length}
                                            options={(cities ?? []).map((c) => ({
                                                label: c.name,
                                                value: c.name,
                                            }))}
                                        />
                                        {touched.city && errors.city && (
                                            <Text fontSize="10px" color="red.500" mt={1}>
                                                {errors.city}
                                            </Text>
                                        )}
                                    </FormControl>
                                </SimpleGrid>

                                <Button
                                    type="submit"
                                    bg={NAVY}
                                    color="white"
                                    _hover={{ bg: NAVY2 }}
                                    borderRadius="xl"
                                    isLoading={isSubmitting || isCreating}
                                    loadingText="Creating customer…"
                                    mt={2}>
                                    Create Customer & Continue
                                </Button>
                            </VStack>
                        </form>
                    )}
                </Formik>
            </Box>
        );
    }

    if (view === 'existing') {
        return (
            <VStack spacing={4} align="stretch">
                <Flex align="center" justify="space-between">
                    <Text fontSize="sm" fontWeight="700" color={NAVY}>
                        Search Existing Customer
                    </Text>
                    <Button
                        variant="ghost"
                        size="sm"
                        color="gray.400"
                        onClick={() => setView('main')}>
                        ← Back
                    </Button>
                </Flex>

                <SearchableSelect
                    name="customers"
                    searchTerm={searchTerm}
                    setSearchTerm={setSearchTerm}
                    options={customers ?? []}
                    loading={isLoading}
                    onSelect={(val) => setSelectedCustomer(val)}
                />

                <Button
                    bg={NAVY}
                    color="white"
                    _hover={{ bg: NAVY2 }}
                    borderRadius="xl"
                    fontWeight="600"
                    fontSize="sm"
                    isDisabled={!selectedCustomer}
                    onClick={() => setView('address')}>
                    Continue
                </Button>
            </VStack>
        );
    }

    return (
        <VStack spacing={4} align="stretch">
            <Text fontSize="sm" fontWeight="700" color={NAVY}>
                Who is this sample request for?
            </Text>

            <Button
                bg={NAVY}
                color="white"
                _hover={{ bg: NAVY2 }}
                borderRadius="xl"
                fontWeight="600"
                fontSize="sm"
                onClick={() => setView('existing')}>
                Existing Customer
            </Button>

            <Button
                variant="outline"
                borderColor={NAVY}
                borderRadius="xl"
                fontWeight="600"
                fontSize="sm"
                onClick={() => setView('new')}>
                New Customer
            </Button>

            <Button variant="ghost" size="sm" color={TEAL} onClick={onSkip}>
                Continue as myself / Skip
            </Button>
        </VStack>
    );
}
